import type { CandidateTopperProfessionalProps } from '@tempmee/react-blocks';
import { IUser } from '~/models';
import { getProfessionalName } from '~/utils/professional';
import {
  isBestInDentalWinner,
  toAttendance,
  toShiftCount,
} from './candidateBadges';
import { toYearsOfExperience } from './candidateCardProps';

/** The topper's data props, with the page's own slots left to the caller. */
export type TBookedProfessionalSignals = Omit<
  CandidateTopperProfessionalProps,
  'topperStyle' | 'roleCode' | 'actions' | 'overflowMenu' | 'testID' | 'actionsTestID'
>;

/**
 * The booked professional → the signals `CandidateTopper` reads.
 *
 * A sibling of `toCandidateCardData`, which maps a *counter offer*: on a
 * terminal shift there is no counter left to read, only
 * `job.offer_final_reciever` — the professional who actually held the shift. The
 * shared per-signal helpers (`toShiftCount`, `toAttendance`,
 * `isBestInDentalWinner`, `toYearsOfExperience`) are reused rather than
 * reimplemented, so the topper grades this professional exactly as the Confirm
 * Candidates page grades an applicant.
 *
 * `attendance` is left `undefined` for an ungraded professional — level 0 is a
 * real state, and since `react-blocks@0.16.0` made the prop optional the badge
 * is simply omitted instead of being given the lowest earned tier.
 *
 * Three of the card's optional signals are deliberately absent:
 *
 * - `missingSpecialty` — a qualification warning on a shift that already ended
 *   asks the office to act on something unactionable.
 * - `partialShift` / `counterRate` — both describe a counter offer's terms, and
 *   there is no counter here.
 *
 * `companyExperience` is absent for a different reason: the Figma draws the
 * chip, but the flags behind it (`hasWorkedWithOffice` / `hasWorkedWithDSO`)
 * live on the counter offer and are missing from `offer_final_reciever`. Raised
 * as a backend gap rather than guessed at from the shift.
 */
export const toBookedProfessionalCardData = (
  professional: IUser | null | undefined,
): TBookedProfessionalSignals => ({
  name: getProfessionalName(professional) ?? '',
  avatarUrl: professional?.profile_image,
  shiftCount: toShiftCount(professional),
  years: toYearsOfExperience(professional),
  rating: professional?.average_rating ?? 0,
  attendance: toAttendance(professional) ?? undefined,
  // The shift payload carries the professional's own favourite flag, unlike the
  // counter offer, where it sits on the offer.
  isFavorite: Boolean(professional?.isFavorite),
  isBestInDental: isBestInDentalWinner(professional),
});
